<script setup lang="ts">
import CarouselItem from '@shared/fondof-ui/components/molecules/carousel/CarouselItem.vue'
import CarouselRoot from '@shared/fondof-ui/components/molecules/carousel/CarouselRoot.vue'
import CarouselContainer from '@shared/fondof-ui/components/molecules/carousel/CarouselScrollContainer.vue'
import CarouselScrollbar from '@shared/fondof-ui/components/molecules/carousel/CarouselScrollbar.vue'
import CarouselViewport from '@shared/fondof-ui/components/molecules/carousel/CarouselViewport.vue'
import { horizontalCarouselButtonVariants } from '@storefront-satch/components/molecules/carousel/variant'
import CarouselPagination from '@storefront-satch/components/molecules/emblaCarousel/CarouselPagination.vue'
import CarouselPrevNextButton from '@storefront-satch/components/molecules/emblaCarousel/CarouselPrevNextButton.vue'
import type {
  TeaserCardItemAdditionalProps,
  TeaserCardItemProps,
} from '@storefront-satch/components/molecules/teaser-card/types'
import { teaserCardItemSliderVariants } from '@storefront-satch/components/molecules/teaser-card/variants'
import type { TeaserCardGroupProps } from '@storefront-satch/components/organisms/teaser-card-group/types'
import { teaserCardGroupGridVariants } from '@storefront-satch/components/organisms/teaser-card-group/variants'
import { useTeaserCard } from '@storefront-satch/composables/teaser-card/useTeaserCard'

const props = defineProps<{
  type: TeaserCardGroupProps['type']
  size: TeaserCardGroupProps['size']
  teaserCardItems: (TeaserCardItemProps & TeaserCardItemAdditionalProps)[]
  selectedItem?: string | null
  showActive: boolean
}>()

const containsLargeRatio = props.teaserCardItems.some(
  (item) => item.mediaAspectRatio === '5/4'
)
const { carouselOptions } = useTeaserCard(props.size)
</script>

<template>
  <div
    v-if="props.type === 'grid'"
    :class="teaserCardGroupGridVariants({ size: props.size })"
  >
    <template v-for="(teaserCardItem, index) in props.teaserCardItems">
      <slot :name="`${teaserCardItem.sys.id}__${index}`" />
    </template>
  </div>

  <CarouselRoot
    v-if="props.type === 'slider'"
    class="w-full"
    :class="{
      'sm:px-[30px] md:px-[110px]': props.size === 'large',
    }"
    :options="carouselOptions"
  >
    <CarouselViewport>
      <CarouselContainer class="px-5 sm:px-0">
        <CarouselItem
          v-for="(teaserCardItem, index) in props.teaserCardItems"
          :key="teaserCardItem?.sys.id"
          :class="[
            teaserCardItemSliderVariants({
              size: props.size,
              width: teaserCardItem?.width ?? 'single',
              isSelected: teaserCardItem?.sys.id === props.selectedItem,
              showActive,
            }),
          ]"
        >
          <slot :name="`${teaserCardItem.sys.id}__${index}`" />
        </CarouselItem>
      </CarouselContainer>

      <CarouselPrevNextButton
        type="prev"
        position="border"
        :class="
          props.size === 'small' && [
            {
              'top-[46px] md:top-[54px] lg:top-[66px]': !containsLargeRatio,
              'top-[62px] md:top-[92px] lg:top-[105px]': containsLargeRatio,
            },
            horizontalCarouselButtonVariants({
              type: 'prev',
              position: 'inside',
            }),
          ]
        "
      />
      <CarouselPrevNextButton
        type="next"
        position="border"
        :class="
          props.size === 'small' && [
            {
              'top-[46px] md:top-[54px] lg:top-[66px]': !containsLargeRatio,
              'top-[62px] md:top-[92px] lg:top-[105px]': containsLargeRatio,
            },
            horizontalCarouselButtonVariants({
              type: 'next',
              position: 'inside',
            }),
          ]
        "
      />
    </CarouselViewport>

    <CarouselPagination class="!mt-10" />
    <CarouselScrollbar class="px-5 pt-8 sm:px-0" />
  </CarouselRoot>
</template>
